/**
 * **A photo, opened** — `docs/visit-page.md` §14, Board 10 B (a lightbox 900
 * wide): the photo with the visit's strip under it; who took it and when; the
 * note; *📍 Taken at the site · 118 Lamp Post Ln* (or amber *Taken 2.1 mi
 * from the site*); *Tags* (one, green when chosen); the switch **Karen sees
 * it on the portal** (*off — crew photos are the office's until someone
 * shares them*, D19); **Download** · **Add to the job's files**.
 *
 * Opened from From the crew's feed and from the Overview's five photos
 * (Slice 1). Writes `PATCH /visit-photos/:id {version, tag | portal}` and
 * `POST /visit-photos/:id/add-to-job {version}`; each answers the tab it was
 * opened over, so the feed and the strip redraw from the server's rows. After
 * the visit closes the tags stay as they are and sharing still works (§2.6).
 *
 * On a desktop and an iPad it is the board's own box over the page; on a
 * phone, Board 16 D's sheet from the bottom (`VisitPopup`), the same parts in
 * one column.
 */
import { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { PHOTO_TAGS, type CrewPhoto, type PhotoTag, type VisitRead } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { useDismiss } from '../components/overlays.js'
import { Switch } from '../components/primitives.js'
import { useVisitWrite, type VisitWriteResult } from './api.js'
import { timeIn } from './format.js'
import { Face } from './face.js'
import { VisitGlyph } from './glyphs.js'
import { VISIT_LAYER, VisitPopup } from './sheet.js'
import { PillChip } from './crew-parts.js'
import { FROM_CREW, LIGHTBOX } from './copy.js'

/** The visit's strip — one photo per moment, oldest first (Board 10 B draws five for eleven). */
function stripOf(photos: readonly CrewPhoto[]): CrewPhoto[] {
  const seen = new Set<string>()
  return [...photos]
    .sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0))
    .filter((photo) => {
      const moment = photo.at.slice(0, 16)
      if (seen.has(moment)) return false
      seen.add(moment)
      return true
    })
}

export function PhotoLightbox({
  visitId, read, photos, openId, customerFirstName, tab = 'from-crew', onClose, onWritten,
}: {
  visitId: string
  read: VisitRead
  /** Every photo the screen has, as the server last sent them. */
  photos: readonly CrewPhoto[]
  /** The photo pressed. */
  openId: string
  /** *Karen* — the job's primary contact (`FromCrewRead.customerFirstName`); null draws no switch. */
  customerFirstName: string | null
  /** The tab it was opened over — its fresh read comes back with each write. */
  tab?: 'from-crew' | 'overview'
  onClose: () => void
  onWritten?: (result: VisitWriteResult) => void
}) {
  const phone = usePhone()
  const write = useVisitWrite(visitId)
  const [currentId, setCurrentId] = useState(openId)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const box = useRef<HTMLDivElement>(null)
  useDismiss(!phone, onClose, box, VISIT_LAYER)

  const strip = useMemo(() => stripOf(photos), [photos])
  const photo = photos.find((candidate) => candidate.id === currentId) ?? photos[0]
  if (!photo) return null
  const tz = read.timeZone

  async function send(method: 'PATCH' | 'POST', path: string, body: unknown) {
    setBusy(true)
    setError(null)
    try {
      const result = await write(method, path, body, { tab })
      onWritten?.(result)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const setTag = (tag: PhotoTag) => {
    if (busy || !photo) return
    void send('PATCH', `/visit-photos/${photo.id}`, { version: photo.version, tag: photo.tag === tag ? null : tag })
  }
  const setPortal = () => {
    if (busy || !photo) return
    void send('PATCH', `/visit-photos/${photo.id}`, { version: photo.version, portal: !photo.portal })
  }
  const addToJob = () => {
    if (busy || !photo) return
    void send('POST', `/visit-photos/${photo.id}/add-to-job`, { version: photo.version })
  }

  const picture = (
    <div className="flex flex-col">
      <span
        className={cn('relative block overflow-hidden rounded-[12px] bg-surface-group',
          phone ? 'aspect-[26/17] w-full' : 'h-[340px] w-[520px]')}
      >
        <img src={photo.file.url} alt={photo.note ?? ''} className="h-full w-full object-cover" />
      </span>
      {strip.length > 1 && (
        <div className={cn('mt-2.5 flex gap-1.5', phone && 'no-scrollbar overflow-x-auto')}>
          {strip.map((thumb) => (
            <button
              key={thumb.id}
              type="button"
              aria-pressed={thumb.id === photo.id}
              onClick={() => setCurrentId(thumb.id)}
              className="relative block h-11 w-[60px] shrink-0 overflow-hidden rounded-[7px] bg-surface-group"
            >
              <img src={thumb.file.url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  )

  const facts = (
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      {!phone && (
        <div className="flex items-center gap-[9px]">
          <Face person={photo.by} size={30} />
          <div className="flex flex-col">
            <b className="text-[13.5px] font-bold text-text">{photo.by.name}</b>
            <span className="text-[12px] text-text-muted">{LIGHTBOX.when(timeIn(tz, photo.at, 'dateAmpm'), String(photo.day))}</span>
          </div>
        </div>
      )}
      {photo.note && <div className="text-[13px] leading-[1.5] text-text">{photo.note}</div>}
      {photo.where && (
        <div className={cn('flex items-center gap-1.5 text-[12px]', photo.where.atSite ? 'text-accent-dark' : 'text-warn')}>
          <VisitGlyph name="pin" size={13} stroke={2} />
          {photo.where.atSite ? LIGHTBOX.takenAt(photo.where.line1) : LIGHTBOX.takenAway(photo.where.miles.toFixed(1))}
        </div>
      )}
      <div className="min-w-0">
        <div className="mb-[5px] text-[11.5px] font-medium text-text-muted">{LIGHTBOX.tags}</div>
        <div className="flex flex-wrap gap-1.5">
          {PHOTO_TAGS.map((tag) => (
            <PillChip key={tag} look={phone ? 'phone' : 'pick'} selected={photo.tag === tag}
              disabled={!read.can.edit} onClick={() => setTag(tag)}>
              {FROM_CREW.tag[tag]}
            </PillChip>
          ))}
        </div>
      </div>
      {customerFirstName && (
        <button
          type="button"
          role="switch"
          aria-checked={photo.portal}
          onClick={setPortal}
          className={cn('flex items-center gap-2.5 rounded-[10px] border border-line px-3 py-2.5 text-left',
            phone && 'min-h-11')}
        >
          <Switch size="sm" checked={photo.portal} onChange={setPortal} label={LIGHTBOX.share(customerFirstName)} hideLabel readOnly />
          <span className="flex flex-col">
            <b className="text-[12.5px] font-bold text-text">{LIGHTBOX.share(customerFirstName)}</b>
            {!photo.portal && <span className="text-[11.5px] text-text-muted">{LIGHTBOX.shareOff}</span>}
          </span>
        </button>
      )}
      {error && <p className="m-0 text-[12px] text-danger">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={photo.file.url}
          download={photo.file.name}
          className={cn('inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-control border border-line',
            'bg-surface px-3.5 text-[12.5px] font-semibold text-text hover:bg-hover', phone ? 'h-11' : 'h-8')}
        >
          <VisitGlyph name="download" size={14.5} stroke={2} />
          {LIGHTBOX.download}
        </a>
        {!photo.inJobFiles && (
          <button
            type="button"
            onClick={addToJob}
            disabled={busy}
            className={cn('inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-control border border-line',
              'bg-surface px-3.5 text-[12.5px] font-semibold text-text hover:bg-hover', phone ? 'h-11' : 'h-8')}
          >
            <VisitGlyph name="doc" size={14.5} stroke={2} />
            {LIGHTBOX.addToJob}
          </button>
        )}
      </div>
    </div>
  )

  if (phone) {
    return (
      <VisitPopup
        width={934}
        title={photo.by.name}
        sub={LIGHTBOX.when(timeIn(tz, photo.at, 'dateAmpm'), String(photo.day))}
        onClose={onClose}
      >
        <div className="flex flex-col gap-3.5">
          {picture}
          {facts}
        </div>
      </VisitPopup>
    )
  }

  return createPortal((
    <div
      style={{ zIndex: VISIT_LAYER }}
      data-overlay-layer={VISIT_LAYER}
      className="fixed inset-0 flex items-center justify-center bg-scrim/25 p-4"
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label={photo.by.name}
        data-as-drawn="vp-10 B"
        className="flex w-[934px] max-w-full gap-[18px] rounded-dialog border border-line bg-surface p-4 leading-[normal] text-text shadow-popover"
      >
        {picture}
        {facts}
      </div>
    </div>
  ), document.body)
}
